import { PhotoCamera } from '@mui/icons-material';
import { Avatar, Box, Button, CircularProgress, IconButton, Typography, useTheme } from '@mui/material';
import { format } from 'date-fns';
import React, { ChangeEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DATE_TIME_FORMAT_STRING } from '../../../common/utilities/TimeframeUtilities';
import { useConfirmation } from "../../../contexts/confirmation-context/ConfirmationContext";
import { useOfflineDetectContext } from "../../../contexts/offline-detect-context/UseOfflineDetectContext";
import { OfflineDetectActionType } from "../../../contexts/offline-detect-context/OfflineDetectContextTypes";
import { useUserContext } from '../../../contexts/user-context/UserContext';
import { useRouteContext } from "../../../hooks/use-route-context/UseRouteContext";
import { useOfflineConfig } from '../../../queries/offline-config/UseOfflineConfig';
import { useOfflineData } from '../../../queries/offline-config/UseOfflineData';
import { useQueryNotifications } from '../../../queries/UseQueryNotifications';
import { useSaveUserImage } from '../../../queries/user-settings/UseSaveUserImage';
import { useUserImage } from '../../../queries/user-settings/UseUserImage';

export default function AccountSettingsHeader() {
   
    const { t } = useTranslation();
    const [userState] = useUserContext();
    const { acknowledge } = useConfirmation();
    const [loadingOfflineData, setLoadingOfflineData] = useState<boolean>(false);
    const [offlineState, dispatchOfflineState] = useOfflineDetectContext();
    const routeContext = useRouteContext();
    
    const theme = useTheme();    

    const {refetch: refreshOfflineData} = useQueryNotifications(useOfflineData)({
        customerID: userState.customer?.id as number,
        enabled: false
    });    
    const {data: offlineConfig} = useQueryNotifications(useOfflineConfig)({});    
    const {data: userImage} = useQueryNotifications(useUserImage)({
        userID: userState.user?.id as number
    });

    const { mutate: saveUserImage } = useQueryNotifications(useSaveUserImage)({
        onSuccess: () => {
            acknowledge(t('profile.userImageUploaded'));
        }
    });

    const onRefreshOfflineData = async () => {

        setLoadingOfflineData(true);

        await refreshOfflineData();

        dispatchOfflineState({
            type: OfflineDetectActionType.OfflineDataLoaded,
            payload: true
        });

        acknowledge(t('profile.offlineDataRefreshed' ));
        
        setLoadingOfflineData(false);

    };

    const onUploadFile = (event: ChangeEvent<HTMLInputElement> ) => {

        if (event.target.files) {
            const file = event.target.files[0];

            saveUserImage({
                groupID: routeContext.groupID as number,
                userID: userState.user?.id as number,
                file
            });
        }
    };

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column'
        }}>
            <Box sx={{ marginLeft: 'auto',
                        marginRight: 'auto'
                }}>
                <Box sx={{
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column'
                }}>
                    <Avatar sx={{ 
                        width: theme.spacing(7),
                        height: theme.spacing(7),
                        marginLeft: 'auto',
                        marginRight: 'auto'
                        }} 
                        alt={userState.user?.displayText} 
                        src={(userImage?.length ?? 0) > 0 ? userImage : undefined} />
                    {
                    !offlineState.offline && 
                    <>
                        <input accept="image/*" style={{display: 'none'}} id="icon-button-file" type="file" onChange={onUploadFile} />
                        <label style={{
                            position: 'absolute',
                            bottom: 0,
                            right: 0
                            }} htmlFor="icon-button-file">
                            <IconButton sx={{ 
                                padding: 0,
                                opacity: .8
                                }}
                                 color="primary" aria-label="upload picture" component="span">
                                <PhotoCamera />
                            </IconButton>
                        </label>
                    </>                        
                    }
                </Box>                    
                <Typography variant='subtitle1' sx={{marginLeft: 'auto', marginRight: 'auto'}} gutterBottom={false}>
                    {userState.user?.displayText}
                </Typography>

                <Typography variant='subtitle2' sx={{marginLeft: 'auto', marginRight: 'auto', textTransform: 'uppercase'}} color='textSecondary' gutterBottom={false}>
                    {t('profile.offlineData')}
                </Typography>

            </Box>




            {
            offlineConfig?.lastRefresh && 
            <Box sx={{marginLeft: 'auto', marginRight: 'auto'}}>
                <Typography variant='subtitle2' sx={{marginLeft: 'auto', marginRight: 'auto'}} color='textSecondary'>
                    {t('profile.lastRefresh')}: {offlineConfig.lastRefresh ? format(new Date(offlineConfig.lastRefresh), DATE_TIME_FORMAT_STRING) : ""}
                </Typography>
            </Box>
            }

            {
            offlineConfig?.lastSync &&
            <Box sx={{marginLeft: 'auto', marginRight: 'auto'}}>
                <Typography variant='subtitle2' sx={{marginLeft: 'auto', marginRight: 'auto'}} color='textSecondary'>
                    {t('profile.lastSync')}: {offlineConfig.lastSync ? format(new Date(offlineConfig.lastSync), DATE_TIME_FORMAT_STRING) : ""}
                </Typography>

            </Box>
            }

            <Box sx={{
                marginLeft: 'auto',
                marginRight: 'auto',         
                position: 'relative',
                marginTop: theme.spacing(1),
                marginBottom: theme.spacing(1)
                }}>
                <Button
                    variant="contained"
                    disabled={loadingOfflineData || offlineState.offline}
                    onClick={onRefreshOfflineData}>
                    {t('profile.refreshOfflineData')}
                </Button>
                {loadingOfflineData && 
                <CircularProgress size={24} sx={{        
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    marginTop: theme.spacing(-1.5),
                    marginLeft: theme.spacing(-1.5),
                }} />
                 } 
            </Box>

        </Box>
    );
  }
